import React, { useCallback } from "react";
import { View, TouchableOpacity, Linking, StyleSheet } from "react-native";
import Text from "@components/CustomText";
import StatusBadge from "./StatusBadge";
import { tokens, sharedStyles } from "../styles";

// Customer card — derived from data.TP entries.
// Shows the customer title (name + crate ids + locality) + tappable
// address + current assignment state. OUT_OF_AREA / UNASSIGN actions are
// surfaced as buttons at the bottom; tap fires the parent's handler.

const CustomerCard = ({
  card,
  cpDbs = [],
  onPressMisroute,
  onPressUnassign,
}) => {
  const lat = Number(card?.lat);
  const lng = Number(card?.lng);
  const hasGeo =
    Number.isFinite(lat) && Number.isFinite(lng) && Boolean(card?.address);
  const ud = (card?.tp_list || [])[0] || {};
  const isMisroute = ud?.cp_db_id === "MISROUTE";
  const isAssigned = ud?.status === "ASSIGNED" && !isMisroute;
  // Resolve the assigned DB name. Snapshot already carries `db_name` on
  // each touchpoint, but the backend's cpDbMap lookup can fall through to
  // `undefined` when the assigned cp_db_id isn't in the active cp_dbs
  // list (stale/deleted DB ref). Fall back to a fresh lookup so the pill
  // doesn't render as a tiny "—" that looks blank on device.
  const resolvedDbName =
    ud?.db_name ||
    cpDbs.find((d) => String(d?.cp_db_id) === String(ud?.cp_db_id))?.db_name ||
    "—";

  const openMaps = useCallback(() => {
    if (!hasGeo) return;
    Linking.openURL(
      `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
    ).catch(() => {});
  }, [hasGeo, lat, lng]);

  return (
    <View style={sharedStyles.card}>
      <Text variant="title_16" style={styles.title}>
        {card?.title || "—"}
      </Text>
      {hasGeo ? (
        <TouchableOpacity activeOpacity={0.7} onPress={openMaps}>
          <Text variant="caption_2" style={styles.address}>
            {card.address}
          </Text>
        </TouchableOpacity>
      ) : null}
      <View style={styles.statusRow}>
        {isMisroute ? (
          <StatusBadge label="Out of Area" state="misroute" />
        ) : isAssigned ? (
          <StatusBadge label={`Assigned: ${resolvedDbName}`} state="packed" />
        ) : (
          <StatusBadge label="Unassigned" state="unpacked" />
        )}
      </View>
      <View style={styles.actionsRow}>
        {!isMisroute && onPressMisroute ? (
          <TouchableOpacity
            activeOpacity={0.85}
            onPress={() => onPressMisroute(card)}
            style={[styles.actionBtn, styles.misrouteBtn]}
          >
            <Text variant="caption_2" style={styles.misrouteText}>
              Mark Out of Area
            </Text>
          </TouchableOpacity>
        ) : null}
        {isAssigned && onPressUnassign ? (
          <TouchableOpacity
            activeOpacity={0.85}
            onPress={() => onPressUnassign(card)}
            style={[styles.actionBtn, styles.unassignBtn]}
          >
            <Text variant="caption_2" style={styles.unassignText}>
              Unassign
            </Text>
          </TouchableOpacity>
        ) : null}
      </View>
    </View>
  );
};

export default CustomerCard;

const styles = StyleSheet.create({
  title: {
    color: tokens.textPrimary,
    fontWeight: "700",
    marginBottom: 4,
  },
  address: {
    color: tokens.info,
    fontSize: 12,
    textDecorationLine: "underline",
    marginBottom: tokens.spacing.small,
  },
  statusRow: { flexDirection: "row", marginBottom: tokens.spacing.small },
  actionsRow: { flexDirection: "row" },
  actionBtn: {
    paddingHorizontal: tokens.spacing.medium,
    paddingVertical: 6,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    marginRight: tokens.spacing.small,
  },
  misrouteBtn: {
    borderColor: tokens.warning,
    backgroundColor: tokens.surface,
  },
  misrouteText: { color: tokens.warning, fontSize: 12, fontWeight: "700" },
  unassignBtn: {
    borderColor: tokens.borderMedium,
    backgroundColor: tokens.surface,
  },
  unassignText: { color: tokens.textPrimary, fontSize: 12, fontWeight: "700" },
});
